/** @jsxRuntime classic */

/** @jsx jsx */
import React, { useEffect, useRef } from 'react';
import { jsx, css } from '@emotion/core';
import Rodal from 'rodal';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'emotion-theming';
import { useToasts } from 'react-toast-notifications';
import { home, migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from '../../Colors';
import useMediaQuery from '../../../hooks/use-media-query';
import ScrollWrapper from '../../ScrollWrapper';
import { Button, FormError, DropDownCombo, TextArea, SpinnerSmall } from '../../../core';
import { DivRow, DivColumn, Title, InputText, FormLabel } from '../InputsUI';
import styles from './modalStyle';
import modalCustomStyle from './utils';
import { useCountries, useIdentificationsTypes, getStateByName, getCityByName, useFindPerson } from '../../../queries';
import useLocation from '../../../hooks/use-locations';
import masksJson from '../../../utils/phoneMask.json';
const themes = {
  home: {
    alpha: home.cerulean,
    beta: home.frenchPass,
    gamma: home.cerulean,
    delta: home.fireEngineRed,
    epsilon: home.white
  },
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.cerulean,
    beta: migratoryWebCitizen.frenchPass,
    gamma: migratoryWebCitizen.cerulean,
    delta: migratoryWebCitizen.fireEngineRed,
    epsilon: migratoryWebCitizen.white
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.deepCerulean,
    beta: sanitarianRegistersWeb.frenchPass,
    gamma: sanitarianRegistersWeb.deepCerulean,
    delta: sanitarianRegistersWeb.fireEngineRed,
    epsilon: sanitarianRegistersWeb.white
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.mantis,
    beta: phytosanitaryWeb.frenchPass,
    gamma: phytosanitaryWeb.mantis,
    delta: phytosanitaryWeb.mantis,
    epsilon: phytosanitaryWeb.sherpaBlue
  },
  tdCarWeb: {
    alpha: tdCarWeb.cerulean,
    beta: tdCarWeb.frenchPass,
    gamma: tdCarWeb.cerulean,
    delta: tdCarWeb.fireEngineRed,
    epsilon: tdCarWeb.white
  }
};
/**
 * This component represents a modal to add new importer
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} onClickSaveImporter Function is triggered when the use button is pressed, and save the new importer.
 * @param {object} formik Formik.
 */

const NewPerson = /*#__PURE__*/React.memo(({
  isVisible,
  setIsVisible,
  onClickSaveManufacter,
  formik,
  isPending,
  title
}) => {
  var _formik$values2, _formik$values3;

  const {
    theme
  } = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  const {
    addToast
  } = useToasts();
  const {
    data: countries = []
  } = useCountries({
    config: {}
  });
  const [isSearchFormSubmitted, setIsSearchFormSubmitted] = React.useState(false);
  const [textError, setTextError] = React.useState(false);
  const [identificationsTypes, setIdentificationsTypes] = React.useState([]);
  const {
    states,
    state,
    cities,
    districts,
    onLocationChange
  } = useLocation();
  const stateRef = useRef(false);
  const cityRef = useRef(false);
  const isFromCR = formik.values.identificationTypeCode === 'identification';
  const crId = React.useMemo(() => countries.find(x => x.ISO31661A3 === 'CRI'), [countries]);
  const [phoneMask, setPhoneMask] = React.useState({});
  useIdentificationsTypes({
    scope: '',
    config: {
      onSuccess(data) {
        setIdentificationsTypes(data === null || data === void 0 ? void 0 : data.filter(({
          code
        }) => ['identification', 'passport'].includes(code)));
      }

    }
  });

  const onChangeState = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      state: name,
      stateId: id,
      cityId: '',
      districtId: ''
    });
    onLocationChange({
      target: {
        value: id,
        name: 'state'
      }
    });
  };

  const onChangeCity = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      cityId: id,
      city: name,
      districtId: ''
    });
    onLocationChange({
      target: {
        value: id,
        name: 'city'
      }
    });
  };

  const onChangeDistrict = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      districtId: id,
      district: name
    });
    onLocationChange({
      target: {
        value: id,
        name: 'district'
      }
    });
  };

  const onChangeIdentificationType = React.useCallback(({
    id,
    code
  }) => {
    var _crId$id;

    formik.setValues({ ...formik.values,
      identificationType: id,
      identificationTypeCode: code,
      countryId: code === 'identification' ? (_crId$id = crId === null || crId === void 0 ? void 0 : crId.id) !== null && _crId$id !== void 0 ? _crId$id : '' : '',
      identification: '',
      firstName: '',
      lastName: '',
      secondLastName: ''
    });
  }, [formik.values, crId]);

  const onChangeNacionality = ({
    id
  }) => {
    formik.setFieldValue('countryId', id);
  };

  useEffect(() => {
    var _formik$values$state;

    async function fetch() {
      const [stateData] = await getStateByName(formik.values.state);

      if (stateData === null || stateData === void 0 ? void 0 : stateData.id) {
        onLocationChange({
          target: {
            value: stateData === null || stateData === void 0 ? void 0 : stateData.id,
            name: 'state'
          }
        });
      }

      stateRef.current = true;
    }

    if (((_formik$values$state = formik.values.state) === null || _formik$values$state === void 0 ? void 0 : _formik$values$state.length) && !stateRef.current) {
      fetch();
    }
  }, [formik.values.state, onLocationChange]);
  useEffect(() => {
    var _state$toString;

    async function fetch() {
      const [cityData] = await getCityByName({
        name: formik.values.city,
        stateId: state
      });

      if (cityData === null || cityData === void 0 ? void 0 : cityData.id) {
        onLocationChange({
          target: {
            value: cityData === null || cityData === void 0 ? void 0 : cityData.id,
            name: 'city'
          }
        });
      }

      cityRef.current = true;
    }

    if ((state === null || state === void 0 ? void 0 : (_state$toString = state.toString()) === null || _state$toString === void 0 ? void 0 : _state$toString.length) && !cityRef.current) {
      fetch();
    }
  }, [formik.values.city, onLocationChange, state]);
  const {
    isFetching,
    data: personData
  } = useFindPerson({
    id: +formik.values.identification,
    options: {
      enabled: isSearchFormSubmitted,

      onSuccess(response) {
        if (!response.data) {
          setTextError('El número de identificación ingresado no existe.');
          formik.setValues({ ...formik.values,
            firstName: '',
            lastName: '',
            secondLastName: ''
          });
        } else {
          formik.setValues({ ...formik.values,
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            secondLastName: response.data.secondLastName
          });
        }
      },

      onSettled() {
        setIsSearchFormSubmitted(false);
      },

      onError(error) {
        setTextError('El número de identificación ingresado no existe.');
        formik.setValues({ ...formik.values,
          firstName: '',
          lastName: '',
          secondLastName: ''
        });
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: true
        });
      }

    }
  });
  useEffect(() => {
    var _formik$values;

    if ((_formik$values = formik.values) === null || _formik$values === void 0 ? void 0 : _formik$values.countryId) {
      const currentCountry = countries.find(x => x.id === formik.values.countryId);
      setPhoneMask(masksJson[currentCountry.ISO31661A2] ? {
        mask: masksJson[currentCountry.ISO31661A2]
      } : {
        regex: '^[0-9]*$'
      });
    }

    formik.setFieldValue('telephone', '');
  }, [formik.values.countryId]);

  const handleIdentificationChange = identification => {
    setTextError('');

    if (isFromCR && identification.length === 9) {
      formik.setFieldValue('identification', identification.toUpperCase());
      setIsSearchFormSubmitted(true);
    } else {
      formik.setFieldValue('identification', identification.toUpperCase());
    }
  };

  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 1112,
    height: 650,
    onClose: () => {},
    customStyles: { ...modalCustomStyle({
        isMobile,
        width: 1112,
        height: 'auto',
        maxHeight: 650
      }),
      borderRadius: '20px',
      overflow: 'hidden'
    }
  }, jsx("div", {
    css: [styles, {
      padding: '35px 46px 0 46px'
    }]
  }, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 30
    }
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      fontSize: 20,
      marginTop: 'unset',
      color: themes[theme].alpha
    }
  }, t(title)))), jsx(ScrollWrapper, null, jsx("form", {
    onSubmit: formik.handleSubmit,
    style: {
      width: '100%',
      maxWidth: 609
    }
  }, jsx(Title, {
    css: {
      fontSize: 14,
      margin: '0 0 25px 0',
      color: themes[theme].alpha,
      borderBottom: `1px solid ${themes[theme].alpha}`,
      paddingBottom: 1,
      display: 'inline-block'
    }
  }, t('Datos personales')), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Tipo de identificación'), ":*"), jsx(DropDownCombo, {
    placeholder: t('Ingresar_placeholder'),
    items: identificationsTypes,
    style: {
      margin: 0,
      width: '100%'
    },
    touched: formik.touched.identificationType,
    error: formik.errors.identificationType,
    value: ((_formik$values2 = formik.values) === null || _formik$values2 === void 0 ? void 0 : _formik$values2.identificationType) || '',
    onChange: onChangeIdentificationType
  }), jsx(FormError, {
    msg: formik.errors.identificationType,
    touched: formik.touched.identificationType
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Número de identificación'), ":*"), jsx(InputText, {
    id: "identification",
    name: "identification",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: handleIdentificationChange,
    value: formik.values.identification,
    touched: formik.touched.identification,
    error: formik.errors.identification,
    disabled: isFetching,
    width: "100%",
    mask: isFromCR ? {
      mask: '9-9999-9999'
    } : {
      regex: '^[a-zA-Z0-9]{0,30}$'
    }
  }, isFetching && jsx(SpinnerSmall, {
    style: {
      width: 20,
      height: 20,
      position: 'absolute',
      top: 16,
      right: 15,
      color: themes[theme].alpha
    }
  })), jsx("span", {
    style: {
      color: '#CE1625',
      fontSize: 12
    }
  }, textError), jsx(FormError, {
    msg: formik.errors.identification,
    touched: formik.touched.identification
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Nombre'), ":*"), jsx(InputText, {
    id: "firstName",
    name: "firstName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.firstName,
    error: formik.errors.firstName,
    touched: formik.touched.firstName,
    mask: {
      regex: '^[A-Za-z-áéíóúüñÁÉÍÓÚÜÑ .,]*$'
    },
    width: "100%",
    disabled: personData,
    maxLength: 255
  }), jsx(FormError, {
    msg: formik.errors.firstName,
    touched: formik.touched.firstName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Primer apellido'), ":*"), jsx(InputText, {
    id: "lastName",
    name: "lastName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.lastName,
    error: formik.errors.lastName,
    touched: formik.touched.lastName,
    mask: {
      regex: '^[A-Za-z-áéíóúüñÁÉÍÓÚÜÑ .,]*$'
    },
    disabled: personData,
    width: "100%",
    maxLength: 255
  }), jsx(FormError, {
    msg: formik.errors.lastName,
    touched: formik.touched.lastName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Segundo apellido'), ":"), jsx(InputText, {
    id: "secondLastName",
    name: "secondLastName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.secondLastName,
    error: formik.errors.secondLastName,
    touched: formik.touched.secondLastName,
    disabled: personData,
    mask: {
      regex: '^[A-Za-z-áéíóúüñÁÉÍÓÚÜÑ .,]*$'
    },
    width: "100%",
    maxLength: 255
  }), jsx(FormError, {
    msg: formik.errors.secondLastName,
    touched: formik.touched.secondLastName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Nacionalidad'), ":*"), jsx(DropDownCombo, {
    placeholder: t('Ingresar_placeholder'),
    items: countries,
    style: {
      margin: 0,
      width: '100%'
    },
    error: formik.errors.countryId,
    touched: formik.touched.countryId,
    value: ((_formik$values3 = formik.values) === null || _formik$values3 === void 0 ? void 0 : _formik$values3.countryId) || '',
    onChange: onChangeNacionality,
    disabled: isFromCR
  }), jsx(FormError, {
    msg: formik.errors.countryId,
    touched: formik.touched.countryId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Número de colegiado'), ":"), jsx(InputText, {
    id: "membershipNumber",
    name: "membershipNumber",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.membershipNumber,
    error: formik.errors.membershipNumber,
    touched: formik.touched.membershipNumber,
    width: "100%",
    mask: {
      regex: '^[A-Za-z-_0-9]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.membershipNumber,
    touched: formik.touched.membershipNumber
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Teléfono'), ":"), jsx(InputText, {
    id: "telephone",
    name: "telephone",
    autoComplete: "off",
    mask: phoneMask,
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.telephone,
    error: formik.errors.telephone,
    touched: formik.touched.telephone,
    width: "100%",
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.telephone,
    touched: formik.touched.telephone
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Correo electrónico'), ":*"), jsx(InputText, {
    id: "email",
    name: "email",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    value: formik.values.email,
    error: formik.errors.email,
    touched: formik.touched.email,
    width: "100%",
    onChange: e => {
      let value = e;

      if (value !== '') {
        value = value.toLowerCase().replace(/[ ]/g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      }

      formik.setFieldValue('email', value);
      return value;
    },
    maxLength: "128"
  }), jsx(FormError, {
    msg: formik.errors.email,
    touched: formik.touched.email
  }))), jsx(Title, {
    css: {
      fontSize: 14,
      margin: '0 0 25px 0',
      color: themes[theme].alpha,
      borderBottom: `1px solid ${themes[theme].alpha}`,
      paddingBottom: 1,
      display: 'inline-block'
    }
  }, t('Dirección')), isFromCR ? jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Provincia'), ":*"), jsx(DropDownCombo, {
    items: states,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeState,
    style: {
      width: '100%'
    },
    value: formik.values.stateId,
    error: formik.errors.stateId,
    touched: formik.touched.stateId
  }), jsx(FormError, {
    msg: formik.errors.stateId,
    touched: formik.touched.stateId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Cantón'), ":*"), jsx(DropDownCombo, {
    items: cities,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeCity,
    style: {
      width: '100%'
    },
    value: formik.values.cityId,
    error: formik.errors.cityId,
    touched: formik.touched.cityId
  }), jsx(FormError, {
    msg: formik.errors.cityId,
    touched: formik.touched.cityId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Distrito'), ":*"), jsx(DropDownCombo, {
    items: districts,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeDistrict,
    style: {
      width: '100%'
    },
    value: formik.values.districtId,
    error: formik.errors.districtId,
    touched: formik.touched.districtId
  }), jsx(FormError, {
    msg: formik.errors.districtId,
    touched: formik.touched.districtId
  })))) : jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Estado'), ":*"), jsx(InputText, {
    id: "stateName",
    name: "stateName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.stateName,
    error: formik.errors.stateName,
    touched: formik.touched.stateName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.stateName,
    touched: formik.touched.stateName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Ciudad'), ":*"), jsx(InputText, {
    id: "cityName",
    name: "cityName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.cityName,
    error: formik.errors.cityName,
    touched: formik.touched.cityName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.cityName,
    touched: formik.touched.cityName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Distrito'), ":*"), jsx(InputText, {
    id: "districtName",
    name: "districtName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.districtName,
    error: formik.errors.districtName,
    touched: formik.touched.districtName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.districtName,
    touched: formik.touched.districtName
  })))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 50
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Otras señas'), ":"), jsx(TextArea, {
    id: "address",
    name: "address",
    autoComplete: "off",
    placeholder: "Ingresar",
    width: "100%",
    maxLength: "150",
    style: {
      height: '170px',
      fontSize: '14px !important'
    },
    value: formik.values.address,
    showCount: true,
    showCountStyle: {
      color: `${themes[theme].beta} !important`,
      bottom: '11px !important',
      right: '19px !important',
      fontSize: '15px !important'
    },
    onChange: formik.handleChange
  }), jsx(FormError, {
    msg: formik.errors.address,
    touched: formik.touched.address
  }))))), jsx("div", {
    css: css`
              position: relative;
              ::before {
                content: '';
                position: absolute;
                top: 0;
                right: -100px;
                left: -100px;
                border-top: 1px solid ${themes[theme].beta};
              }
            `
  }), jsx("div", {
    className: "button-container",
    style: {
      paddingTop: 15,
      paddingBottom: 3
    }
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].gamma,
      border: `2px solid ${themes[theme].gamma}`,
      width: 139
    } // disabled={isCanceling}
    ,
    onClick: setIsVisible
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].delta,
      color: themes[theme].epsilon,
      width: 139
    },
    onClick: onClickSaveManufacter
  }, t('Guardar'), isPending && jsx(SpinnerSmall, {
    style: {
      width: 21,
      height: 21,
      marginLeft: 5
    }
  })))));
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
export default NewPerson;